import { EmptyState } from "@/components/feedback/empty-state";
import { formatRelativeTime } from "@/lib/format/relative-time";
import { decisionMeta } from "@/lib/feedback/review-decision";

export interface FeedbackItem {
  id: string;
  userName: string;
  message: string;
  category: string;
  createdAt: Date | string;
  deviceInfo: string | null;
  /** do_now items come first from the server; shown with a badge. */
  reviewDecision?: string | null;
}

export interface FeedbackQueueCardProps {
  data: FeedbackItem[];
  now: Date;
  onReply?: (id: string) => void;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map(p => p[0]?.toUpperCase() ?? "")
    .join("") || "??";
}

function chipToneFor(category: string): string {
  const c = category.toLowerCase();
  if (c === "bug") return "text-fc-bad border-fc-bad/40 bg-fc-bad/10";
  if (c === "feature") return "text-fc-info border-fc-info/40 bg-fc-info/10";
  if (c === "polish") return "text-fc-warn border-fc-warn/40 bg-fc-warn/10";
  if (c === "praise") return "text-fc-brand border-fc-brand/40 bg-fc-brand/10";
  return "text-fc-text-secondary border-fc-border bg-fc-overlay";
}

export function FeedbackQueueCard({
  data,
  now,
  onReply,
}: FeedbackQueueCardProps) {
  if (data.length === 0) {
    return <EmptyState title="No feedback awaiting reply" />;
  }

  return (
    <div className="flex flex-col">
      {data.map(item => (
        <div
          key={item.id}
          className="grid grid-cols-[34px_1fr_auto_auto] items-center gap-3 border-b border-fc-border py-2.5 last:border-b-0"
        >
          <div className="grid h-[34px] w-[34px] place-items-center rounded-full border border-fc-border bg-fc-overlay text-[11.5px] text-fc-text-secondary">
            {initials(item.userName)}
          </div>
          <div className="min-w-0">
            <div className="truncate text-[12.5px] text-fc-text">
              {item.message}
            </div>
            <div className="truncate text-[11px] text-fc-text-dim">
              {item.userName}
              {item.deviceInfo ? ` · ${item.deviceInfo}` : ""} ·{" "}
              <span className="font-mono">
                {formatRelativeTime(item.createdAt, now)}
              </span>
            </div>
          </div>
          <span className="flex items-center gap-1.5">
            {item.reviewDecision === "do_now" ? (
              <span
                className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] ${decisionMeta("do_now")!.chip}`}
              >
                {decisionMeta("do_now")!.short}
              </span>
            ) : null}
            <span
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${chipToneFor(item.category)}`}
            >
              {item.category}
            </span>
          </span>
          <button
            type="button"
            onClick={() => onReply?.(item.id)}
            className="rounded-md border border-fc-border bg-fc-raised px-2.5 py-1 text-[11.5px] text-fc-text hover:border-fc-brand"
          >
            Reply
          </button>
        </div>
      ))}
    </div>
  );
}
